import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { AppIcons } from '../../../shared/app.icons';
import { AppSettings } from '../../../shared/app.settings';
import { MITableComponent } from '../../../shared/components/mi-table/mi-table.component';
import type { Column } from '../../../shared/models/attribute.models';
import type { Country } from '../../../shared/models/country';
import type { Language } from '../../../shared/models/language';
import type { Pagination } from '../../../shared/models/listResponse.models';
import { CommonBindingDataService } from '../../../shared/services/common-binding-data.service';
import { EntityService } from '../../../shared/services/entity.service';
import { EntityCountsResponse, EntityList } from 'app/modules/shared/models/entity.models';

import { TranslateModule } from '@ngx-translate/core';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { forkJoin } from 'rxjs';
import { ModuleHeaderComponent } from '../../../shared/components/module-header/module-header.component';
import { ConfigService } from '../../../shared/services/config.service';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { ListResponse } from 'app/modules/shared/models/listResponse.models';

interface SponsorTableRow {
  id: string;
  driverGroup: string;
  sponsor_status: Record<string, string>;
  [key: string]: unknown;
}

interface AttributeField {
  attributeCode: string;
  attributeValue: string;
  isDisable: boolean;
  attributeId: number;
  [key: string]: unknown;
}

interface AttributeData {
  tabs: Array<{
    groups: Array<{
      fields: AttributeField[];
    }>;
  }>;
}

interface ZoneData {
  zoneId: string;
  zoneName: string;
  aliasName: string;
}

interface RelatedData {
  values: {
    first_name: string;
    last_name: string;
  };
}

interface EntityData {
  id: string;
  values: Record<string, unknown>;
  relatedData?: RelatedData[];
}

interface MultiApiPayload {
  url: string;
  inputBody: string;
  method: string;
  outputKey: string;
  headers: Record<string, string | number>;
}

interface ExtendedColumn extends Column {
  attributeCode?: string;
  attributeIndex?: number;
  attributeId?: number;
  presetValues?: Array<{ labelKey: string; labelValue: string }>;
}

interface SortColumnDetails {
  attributeId: string;
  sortOrder: number;
}

interface FilterEvent {
  first: number;
  [key: string]: unknown;
}

interface TabViewEvent {
  index: number;
}

interface BulkDeleteEvent {
  entityIds: string[];
}

@Component({
  selector: 'app-sponsors-list',
  standalone: true,
  imports: [BreadcrumbModule, ModuleHeaderComponent, TabViewModule, TranslateModule, MITableComponent, MITableComponent, AccessProviderDirective],
  templateUrl: './sponsors-list.component.html',
  styleUrl: './sponsors-list.component.scss'
})
export class SponsorsListComponent implements OnInit {
  @ViewChild('uploader', { static: true }) uploader: ElementRef<HTMLElement>;
  @ViewChild(MITableComponent) tableRef: MITableComponent;

  miIcons = AppIcons;
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns: ExtendedColumn[] = [];
  cols: ExtendedColumn[] = [];
  moduleName = 'sponsor';
  filterList: ExtendedColumn[];
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels: Record<string, string> = {};
  filterAttributeLabels: Record<string, string> = {};
  listColumns: Record<string, unknown>;
  allColumns: ExtendedColumn[];
  pagination: Pagination;
  totalRecords: number = 0;
  tableData: SponsorTableRow[] = [];
  buttons: Array<Record<string, unknown>> = [];
  country: Country;
  language: Language;
  entityCount: EntityCountsResponse;
  itemsPerPage: number;
  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    actionStatus: ''
  };
  routePath: MenuItem[] = [];
  deleteSponsorMsg: string;
  deleteSponsorHeaderMsg: string;
  showMenu: boolean = false;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  showImportExportBtn: boolean = false;
  filterFieldsArray: AttributeField[];
  isReady: boolean = false;
  attributeData: AttributeData;
  data: AttributeData;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  currentRow: SponsorTableRow;
  visiblePrintLabel: boolean = false;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  bulkUpdateHeader: string;
  reviewHeader: string;
  btnUpdate: string;
  entityCreatedAtAttributeId: string;
  regionGeofences: string[] = [];
  zoneIds: ZoneData[] = [];
  isEntity = true;
  sponsorStatusAttributeId: string;

  constructor(private sponsorService: EntityService,
    private cs: CommonBindingDataService, private confirmationService: ConfirmationService,
    private router: Router, private messageService: MessageService, private actRoute: ActivatedRoute,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
    private configService: ConfigService, private permissionAccessService: PermissionAccessService,
    private cd: ChangeDetectorRef, private loaderService: LoaderService,
  ) { }

  ngOnInit() {
    this.initializeLabels();
    this.initializeRowItems();
    this.initializeBulkSelectionsItems();
    this.initializeOtherVariablesAndAPI();
    this.getData();
    this.getMetaData();
  }

  getMetaData() {
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res: Record<string, { responseBody: string }>) => {
      const advanceFilter = JSON.parse(res.advancedFilter.responseBody);
      if (advanceFilter) {
        this.setSponsorFilterView(advanceFilter);
      }

      const sort = JSON.parse(res.sort.responseBody);
      const entityCreatedAt = sort.tabs[0].groups[0].fields.find((field: AttributeField) => field.attributeCode === AppSettings.ENTITY_CREATED_AT);
      this.entityCreatedAtAttributeId = entityCreatedAt.attributeId;
      if (this.entityCreatedAtAttributeId) {
        // this.entityData.defaultSortColumn = this.entityCreatedAtAttributeId;
        this.entityData.defaultSortType = AppSettings.DESC_SORT_ORDER;
      }

      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setSponsorTableView(defaultTableView);
        this.searchEntity();
      }
      this.cs.applyFilterToColumn(defaultTableView, sort)

    });
  }

  private getMultiApiPayload(): MultiApiPayload[] {
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.SPONSOR}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.ADVANCED_FILTER,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.SPONSOR}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.SPONSOR}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.BULK_UPDATE_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.BULK_UPDATE,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.SPONSOR}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
    ]
  }

  private initializeRowItems() {
    const isEdit = this.permissionAccessService.hasAccess('SPONSORS', '', 'ALL', 'EDIT');
    const isDelete = this.permissionAccessService.hasAccess('SPONSORS', '', 'ALL', 'DELETE');
    const isActivate = this.permissionAccessService.hasAccess('SPONSORS', '', 'ALL', 'ACTIVATE');
    this.rowItems = [
      ...(isEdit ? [{
        label: "Edit",
        icon: `${AppIcons.EDIT_2} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'Edit',
        command: () => {
          this.update();
        }
      }] : []),
      ...(isActivate ? [{
        label: this.cs.getLabel('sponsor.lbl_activate'),
        icon: `${AppIcons.CIRCLE_CHECKED} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'Active',
        command: () => {
          this.changeStatus('Active', this.cs.getLabel('sponsor.lbl_activate'));
        }
      }] : []),
      ...(isActivate ? [{
        label: this.cs.getLabel('sponsor.lbl_deactivate'),
        icon: `${AppIcons.ACTIVATE} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'Inactive',
        command: () => {
          this.changeStatus('Inactive', this.cs.getLabel('sponsor.lbl_deactivate'));
        }
      }] : []),
      { separator: true },
      ...(isDelete ? [{
        label: 'Delete',
        icon: `${AppIcons.BASIC_TRAY} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'Delete',
        command: () => {
          this.tableRef.delete(AppSettings.DELETE_TYPE.ROW);
        }
      }] : []),
    ];
  }

  changeStatus(status: string, acceptLabelText: string) {
    const isActive = status === 'Active' ? true : false;
    this.confirmationService.confirm({
      header: this.cs.getLabel(isActive ? 'sponsor.lbl_activate_sponsor' : 'sponsor.lbl_deactivate_sponsor'),
      message: this.cs.getLabel(isActive ? 'sponsor.msg_activate_confirmation' : 'sponsor.msg_deactivate_confirmation'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: acceptLabelText,
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const requestBody = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: this.moduleName,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [{
            attributeId: this.sponsorStatusAttributeId,
            attributeValue: status
          }],
          relationshipData: [],
          viewCode: AppSettings.VIEW_CODE.STATUS_UPDATE_VIEW,
        };

        this.sponsorService.saveAttributeData(requestBody.entityCode, this.currentRow.id, requestBody).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: status === 'Active' ?
                this.cs.getLabel('sponsor.msg_activated_successfully') :
                this.cs.getLabel('sponsor.msg_deactivated_successfully')
            });
            setTimeout(() => {
              this.searchEntity();
            }, 300);
          }
        });
      },
      reject: () => {
      }
    });
  }

  private initializeBulkSelectionsItems() {
    const isBulkAction = this.permissionAccessService.hasAccess('SPONSORS', '', 'ALL', 'BULK_ACTION');
    const isPrintLabels = this.permissionAccessService.hasAccess('SPONSORS', '', 'ALL', 'PRINT');
    this.bulkSelectionsItems = [
      ...(isBulkAction ? [{
        label: this.cs.getLabel('sponsor.lbl_update'),
        icon: `${AppIcons.EDIT_2} wh-122`,
        iconClass: 'bulk-update-icon',
        styleClass: 'bulk-update-list',
        command: (e: { item: { label: string } }) => {
          this.cd.detectChanges();
          if (e.item.label === this.cs.getLabel('sponsor.lbl_update')) {
            this.openBulkUpdateDialog();
          }
        }
      }] : []),
      ...(isPrintLabels ? [{
        label: this.cs.getLabel('sponsor.print_label'),
        icon: `${AppIcons.MI_PRINT} wh-122`,
        iconClass: 'bulk-update-icon',
        styleClass: 'bulk-update-list',
        command: () => {
          this.handleBulkPrint();
        },
      }] : []),
      {
        separator: true,
      },
      ...(isBulkAction ? [{
        label: this.cs.getLabel('sponsor.delete'),
        icon: `${AppIcons.BASIC_TRAY} wh-122`,
        iconClass: 'bulk-update-icon',
        styleClass: 'bulk-update-list',
        command: () => {
          this.tableRef.delete(AppSettings.DELETE_TYPE.BULK);
        },
      }] : []),
    ];
  }

  private openBulkUpdateDialog() {
    this.showBulkUpdateDialog = false;
    this.cd.detectChanges();
    setTimeout(() => {
      this.showBulkUpdateDialog = true;
      this.cd.detectChanges();
      this.tableRef.showBulkRowDialog();
    }, 0);
  }

  handleBulkUpdate() {
    this.visible = true;
    this.showBulkUpdateDialog = true;
  }

  handleBulkPrint() {
    this.visiblePrintLabel = true;
  }

  downloadImportTemplate() {
    this.sponsorService.downloadImportTemplate(AppSettings.ENTITY_CODE.SPONSOR).subscribe(result => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(result);
      a.download = 'Import Template.xlsx';
      a.click();
    },
      () => {
      });
  }

  downloadImportGuide() {
    this.sponsorService.downloadImportGuide(AppSettings.ENTITY_CODE.SPONSOR).subscribe(result => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(result);
      a.download = 'Import Guide.xlsx';
      a.click();
    },
      () => {
      });
  }

  private initializeLabels() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.bulkUpdateHeader = this.cs.getLabel('lbl_update_sponsors');
    this.reviewHeader = this.cs.getLabel('lbl_review_sponsors');
    this.btnUpdate = this.cs.getLabel('btn_update_sponsors2');
    this.routePath = [{ label: "Sponsors", routerLink: 'app/sponsors' }];
  }

  private initializeOtherVariablesAndAPI() {
    this.btnLabel = this.cs.getLabel('Add Sponsor');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.deleteSponsorMsg = this.cs.getLabel('sponsor.confirm_delete_msg');
    this.deleteSponsorHeaderMsg = this.cs.getLabel('sponsor.confirm_delete_header');
  }

  public onChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      for (const file of Array.from(target.files)) {
        if (file) {
          if (file.size > AppSettings.FILE_UPLOAD_MAX_SIZE_IN_BYTE) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: this.cs.getLabel('err_file_size_exceed_msg') });
          } else {
            const formData = new FormData();
            formData.append("file", file);
            formData.append('forTenantCode', this.configService.getForTenantCode());
            formData.append('entityCode', AppSettings.ENTITY_CODE.SPONSOR);
            formData.append('viewCode', AppSettings.VIEW_CODE.EXCEL_IMPORT_VIEW);
            this.cs.importEntity(formData, AppSettings.ENTITY_CODE.SPONSOR).subscribe(() => {
              this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('lbl_file_uploaded_successfully') });
            })
          }
        }
      }
    }
  }

  getAttributeData() {
    this.sponsorService.getAttributeDefinition(AppSettings.ENTITY_CODE.SPONSOR, AppSettings.VIEW_CODE.BULK_UPDATE_VIEW).subscribe((res: AttributeData) => {
      if (res) {
        this.setSponsorView(res);
      }
    });
  }

  setSponsorView(res: AttributeData) {
    this.attributeData = res;
    this.isLoading = false;
    this.filterFieldsArray = res.tabs[0].groups[0].fields;
    this.filterFieldsArray.forEach(element => {
      switch (element.attributeCode) {
        case 'sponsor_type':
          element.attributeValue = 'Type';
          element.isDisable = false;
          break;
        case 'sponsor_status':
          element.attributeValue = 'Status';
          element.isDisable = false;
          break;
      }
    });
  }

  setCurrentRowData(event: SponsorTableRow) {
    this.currentRow = event;
    const status = Object.entries(event?.sponsor_status)[0][0];
    this.disableStatusAction(status);
  }

  disableStatusAction(data: string) {
    this.rowItems.forEach(rowItem => {
      rowItem.disabled = rowItem.value === data;
    })
  }

  update() {
    this.onUpdateSponsor(this.currentRow.id);
  }

  getTableView() {
    this.sponsorService.getAttributeDefinition(AppSettings.ENTITY_CODE.SPONSOR, AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW, false).subscribe((res: AttributeData) => {
      if (res) {
        this.setSponsorTableView(res);
      }
    });
  }

  setSponsorTableView(res: AttributeData) {
    this.isLoading = false;
    this.data = res;
    this.attributeLabels = this.cs.getAttributeLabels(this.data) as unknown as Record<string, string>;
    this.sponsorStatusAttributeId = res.tabs[0].groups[0].fields.find((field: AttributeField) => field.attributeCode === 'sponsor_status')?.attributeId?.toString();

    this.allColumns = this.cs.getColumns(this.data).filter(column =>
      !['spn_addr_name', 'spn_addr_line_2', 'spn_street', 'spn_city', 'spn_state', 'spn_zip'].includes(column.attributeCode)
    );

    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      this.allColumns[index].header = this.cs.getLabelValue(
        `sponsor` + `.fields.${key.attributeCode}.label`,
        this.attributeLabels,
        key.attributeCode
      );
      if (key.attributeCode === 'spn_addr_line_1') {
        this.allColumns[index].header = this.cs.getLabel('lbl_sponsor_address');
      }
    });
    this.filterAvailableColumns = _.clone(this.allColumns);
  }

  getFilterView() {
    this.sponsorService.getAttributeDefinition(AppSettings.ENTITY_CODE.SPONSOR, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW).subscribe((filterResponse: AttributeData) => {
      if (filterResponse) {
        this.setSponsorFilterView(filterResponse);
      }
    });
  }

  setSponsorFilterView(filterResponse: AttributeData) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse) as unknown as Record<string, string>;
    this.filterList = this.cs.getColumns(filterResponse);
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');
  }

  getData() {
    return forkJoin({
      tableViewData: this.getTableView(),
      filterViewDate: this.getFilterView()
    })
  }

  onSortData(sortColumnDetails: SortColumnDetails) {
    this.entityData.defaultSortColumn = sortColumnDetails.attributeId;
    this.entityData.defaultSortType = (sortColumnDetails.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  searchEntity() {
    this.tableData = [];
    this.entityData.countryCode = this.country[0].countryCode;
    this.loaderService.hide();
    this.sponsorService.searchEntity(AppSettings.ENTITY_CODE.SPONSOR, this.entityData, false).subscribe((res: ListResponse) => {
      res?.data.forEach((region: EntityData) => {
        this.regionGeofences.push(region?.values?.region_geofence as string);
      });
      this.listColumns = res?.data[0]?.values;
      this.pagination = res?.pagination;
      this.totalRecords = res?.count;
      res.data?.forEach((data: EntityData) => {
        this.zoneIds.forEach(element => {
          if (element.zoneId === data?.values.region_geofence) {
            data.values.temp_region_geofence = data?.values.region_geofence;
            data.values.region_geofence = `${element.zoneName} (${element.aliasName})`
          }
        });
        this.tableData.push({
          id: data.id,
          driverGroup: data.relatedData?.map(ele => {
            return `${ele.values.first_name} ${ele.values.last_name}`
          }).join(', '),
          sponsor_status: data.values.sponsor_status as Record<string, string>,
          ...data.values,
        });
      });
      this.loaderService.hide();
    })
  }

  onAddSponsor() {
    this.router.navigate(['app/sponsors/add-sponsor',]);
  }

  getLabel(params: string) {
    return `${params}hi`;
  }

  onRowClicked() {
  }

  onActionBtnClicked() {
  }

  onPageChange(event: { first: number; rows: number }) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event: number) {
    this.itemsPerPage = event;
    this.entityData.limit = this.itemsPerPage;
    this.searchEntity();
  }

  onConfirmDelete() {
  }

  onSponsorDelete(event: string) {
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: country[0].countryCode,
      entityCode: AppSettings.ENTITY_CODE.SPONSOR,
      entityIds: [
        event
      ]
    };
    this.sponsorService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'sponsor', AppSettings.MSG_ACTION.DELETE, 'success');
        setTimeout(() => {
          this.searchEntity();
        }, 300);
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'sponsor', AppSettings.MSG_ACTION.DELETE, 'error');
      }
    })
  }

  onUpdateSponsor(sponsorId: string) {
    this.router.navigate(['app/sponsors/add-sponsor/'], { queryParams: { id: sponsorId } });
  }

  onRowSelect(event: { type: string; data: SponsorTableRow }) {
    if (event.type === 'row') {
      this.router.navigate(['app/sponsors/sponsor-details'], { queryParams: { tab: 'overview', id: event.data.id } });
      this.sponsorService.entityCurrentRow.next(event.data);
    }
  }

  onFilterValueChange(event: FilterEvent) {
    const vData = event;
    this.entityData.filters = [];
    for (const [key, value] of Object.entries(vData)) {
      const attributeValue = value;
      if (attributeValue) {
        this.entityData.filters.push({
          attributeCode: key,
          attributeValue
        });
      }
    }
    this.entityData.filters = _.filter(_.uniq(this.entityData.filters, (item) => {
      if (item.attributeValue.length > 0) {
        return item.attributeCode;
      }
    }), (element) => {
      if (element.attributeValue.length > 0) {
        return true;
      }
      return false;
    });
    this.entityData.offset = event.first;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');
    this.searchEntity();
  }

  onSearchValueChanges(event: string) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  tabViewChange(event: TabViewEvent) {
    this.activeIndex = event.index;
    const actionStatus = '';
    switch (event.index) {
      case 0:
        this.entityData.actionStatus = actionStatus;
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef.clearFilter();
        this.searchEntity();
        break;
      case 1:
        this.entityData.actionStatus = AppSettings.VEHICLE_TAB_LIST.ASSIGNED;
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef.clearFilter();
        this.searchEntity();
        break;

      case 2:
        this.entityData.actionStatus = AppSettings.VEHICLE_TAB_LIST.UNASSIGNED;
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef.clearFilter();
        this.searchEntity();
        break;

      case 3:
        this.entityData.actionStatus = AppSettings.VEHICLE_TAB_LIST.DELETE
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_DELETED;
        this.entityData.filters = [];
        this.tableRef.clearFilter();
        this.searchEntity();
        break;

      default:
        break;
    }
    if (event.index === 0 || event.index === 1 || event.index === 2) {
      this.showBulkUpdateDialog = false;
    }
  }

  onBulkDataUpdateEvent() {
    this.searchEntity();
  }

  onBulkDataDeleteEvent(event: BulkDeleteEvent) {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_CODE.SPONSOR,
      countryCode: this.country[0].countryCode,
      entityIds: event.entityIds
    };
    this.sponsorService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'sponsor', AppSettings.MSG_ACTION.BULK_DELETE, 'success');
        this.searchEntity();
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'sponsor', AppSettings.MSG_ACTION.BULK_DELETE, 'error');
      }
    });
  }
}


